export const LEG_COLORS = ['#fbbf24', '#38bdf8', '#4ade80', '#f472b6', '#a78bfa', '#fb923c', '#34d399', '#e879f9'];

export const isValidCoordinate = (lat: number | null | undefined, lng: number | null | undefined): boolean => {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return false;
  if (isNaN(lat) || isNaN(lng)) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

export const formatAgeMinutes = (iso?: string) => {
  if (!iso) return '--';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '--';
  return `${Math.floor((Date.now() - ts) / 60000)}m`;
};

export const formatTime = (iso?: string) => {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const haversineNm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 3440.065;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const normalizeBearing = (bearingDeg: number) => ((bearingDeg % 360) + 360) % 360;

export const bearingDegrees = (fromLat: number, fromLng: number, toLat: number, toLng: number): number => {
  const phi1 = fromLat * Math.PI / 180;
  const phi2 = toLat * Math.PI / 180;
  const lambda1 = fromLng * Math.PI / 180;
  const lambda2 = toLng * Math.PI / 180;
  const y = Math.sin(lambda2 - lambda1) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(lambda2 - lambda1);
  return normalizeBearing(Math.atan2(y, x) * 180 / Math.PI);
};

const cardinal8 = (bearingDeg: number): 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' => {
  const dirs: Array<'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW'> = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const idx = Math.round(normalizeBearing(bearingDeg) / 45) % 8;
  return dirs[idx];
};

type AirstripRef = { lat: number; lng: number; name?: string | null; code?: string | null; icao?: string | null };

const preferredAirstripDisplayName = (airstrip: AirstripRef): string => {
  const code = (airstrip.code || airstrip.icao || '').toUpperCase();
  const rawName = (airstrip.name || '').trim();
  const hasName = rawName.length > 0;

  if (code === 'HTKJ') return 'Kilimanjaro Int.';
  if (code === 'HTAR') return 'Arusha';
  if (code === 'HTLM') return 'Manyara';

  if (hasName) {
    if (/international/i.test(rawName)) return rawName.replace(/\bInternational\b/i, 'Int.');
    if (/\bint\.?$/i.test(rawName)) return rawName;
    if (code && rawName.toLowerCase() === code.toLowerCase()) return rawName;
    return code ? `${code} · ${rawName}` : rawName;
  }

  return code || 'Unknown';
};

export const formatRelativeAirstripLocation = (
  lat: number | null | undefined,
  lng: number | null | undefined,
  airstrips: AirstripRef[]
): { primary: string; secondary: string | null } | null => {
  if (!Number.isFinite(lat as number) || !Number.isFinite(lng as number) || !airstrips.length) return null;

  let nearest: AirstripRef | null = null;
  let minNm = Number.POSITIVE_INFINITY;
  for (const a of airstrips) {
    if (!Number.isFinite(a.lat) || !Number.isFinite(a.lng)) continue;
    const d = haversineNm(lat as number, lng as number, a.lat, a.lng);
    if (d < minNm) {
      minNm = d;
      nearest = a;
    }
  }
  if (!nearest || !Number.isFinite(minNm)) return null;

  const locationName = preferredAirstripDisplayName(nearest);
  const coords = `${(lat as number).toFixed(4)}, ${(lng as number).toFixed(4)}`;
  if (minNm < 0.5) return { primary: locationName, secondary: coords };

  const inboundBearing = bearingDegrees(nearest.lat, nearest.lng, lat as number, lng as number);
  const roundedNm = Math.max(1, Math.round(minNm));
  return {
    primary: `${roundedNm} nm ${cardinal8(inboundBearing)} of ${locationName}`,
    secondary: coords,
  };
};

export type RunwayHeadingInfo = {
  runwayAHeadingDeg: number;
  runwayBHeadingDeg: number;
  runwayALabel: string;
  runwayBLabel: string;
};

export type AirstripRunwayWindCondition = 'GOOD' | 'CAUTION' | 'RISK' | 'UNKNOWN';

export type RunwayWindAdvisory = {
  bestRunway: string | null;
  bestRunwayHeadingDeg: number | null;
  headwindComponentKt: number | null;
  crosswindComponentKt: number | null;
  tailwindComponentKt: number | null;
  condition: AirstripRunwayWindCondition;
};

const normalizeHeading = (deg: number) => {
  const n = ((deg % 360) + 360) % 360;
  return n === 0 ? 360 : n;
};

const runwayNumberToHeading = (runwayNumberText: string): number | null => {
  const n = Number.parseInt(runwayNumberText, 10);
  if (!Number.isFinite(n) || n < 1 || n > 36) return null;
  if (n === 36) return 360;
  return n * 10;
};

export const parseRunwayHeadings = (runwayValue?: string | null): RunwayHeadingInfo | null => {
  if (!runwayValue || typeof runwayValue !== 'string') return null;
  const match = runwayValue.trim().match(/^(\d{2})\s*\/\s*(\d{2})$/);
  if (!match) return null;

  const runwayAHeadingDeg = runwayNumberToHeading(match[1]);
  const runwayBHeadingDeg = runwayNumberToHeading(match[2]);
  if (!runwayAHeadingDeg || !runwayBHeadingDeg) return null;

  return {
    runwayAHeadingDeg,
    runwayBHeadingDeg,
    runwayALabel: match[1],
    runwayBLabel: match[2],
  };
};

const headingDeltaDeg = (fromDeg: number, toDeg: number) => {
  let diff = Math.abs(normalizeHeading(fromDeg) - normalizeHeading(toDeg));
  if (diff > 180) diff = 360 - diff;
  return diff;
};

const componentsForRunway = (windDirectionDeg: number, windSpeedKt: number, runwayHeadingDeg: number) => {
  const delta = headingDeltaDeg(windDirectionDeg, runwayHeadingDeg);
  const radians = (delta * Math.PI) / 180;
  const along = windSpeedKt * Math.cos(radians);
  const cross = Math.abs(windSpeedKt * Math.sin(radians));
  return {
    headwindComponentKt: along >= 0 ? along : 0,
    tailwindComponentKt: along < 0 ? Math.abs(along) : 0,
    crosswindComponentKt: cross,
  };
};

const advisoryConditionFromComponents = (headwindKt: number, crosswindKt: number, tailwindKt: number): AirstripRunwayWindCondition => {
  if (tailwindKt > 5 || crosswindKt > 18) return 'RISK';
  if ((crosswindKt > 10 && crosswindKt <= 18) || tailwindKt > 0) return 'CAUTION';
  if (headwindKt >= 0 && crosswindKt <= 10) return 'GOOD';
  return 'UNKNOWN';
};

export const getRunwayWindAdvisory = (
  runwayValue: string | null | undefined,
  windDirectionDeg: number | null | undefined,
  windSpeedKt: number | null | undefined
): RunwayWindAdvisory => {
  const parsed = parseRunwayHeadings(runwayValue);
  if (!parsed || !Number.isFinite(windDirectionDeg as number) || !Number.isFinite(windSpeedKt as number)) {
    return {
      bestRunway: null,
      bestRunwayHeadingDeg: null,
      headwindComponentKt: null,
      crosswindComponentKt: null,
      tailwindComponentKt: null,
      condition: 'UNKNOWN',
    };
  }

  const windDir = normalizeHeading(windDirectionDeg as number);
  const windKt = Math.max(0, windSpeedKt as number);
  const a = componentsForRunway(windDir, windKt, parsed.runwayAHeadingDeg);
  const b = componentsForRunway(windDir, windKt, parsed.runwayBHeadingDeg);

  const chooseA =
    a.headwindComponentKt > b.headwindComponentKt ||
    (a.headwindComponentKt === b.headwindComponentKt && a.tailwindComponentKt < b.tailwindComponentKt);
  const chosen = chooseA ? a : b;
  const chosenLabel = chooseA ? parsed.runwayALabel : parsed.runwayBLabel;
  const chosenHeading = chooseA ? parsed.runwayAHeadingDeg : parsed.runwayBHeadingDeg;

  return {
    bestRunway: chosenLabel,
    bestRunwayHeadingDeg: chosenHeading,
    headwindComponentKt: chosen.headwindComponentKt,
    crosswindComponentKt: chosen.crosswindComponentKt,
    tailwindComponentKt: chosen.tailwindComponentKt,
    condition: advisoryConditionFromComponents(chosen.headwindComponentKt, chosen.crosswindComponentKt, chosen.tailwindComponentKt),
  };
};
